import { useState, useEffect, useRef, useContext } from 'react'

import {
  Box
} from '@mui/material'

import Grid from '@mui/material/Grid'

// Context
import { MapDataContext, MapDispatchContext } from 'src/store/map/Store'

import { setPlayerPosition } from 'src/store/map/actions'

// hooks
import usePlayerControls from 'src/hooks/usePlayerControls'
import useResting from 'src/hooks/useResting'
import useLoadMapGrid from 'src/hooks/useLoadMapGrid'

// Components
import Tile from 'src/components/common//Tile'
import Player from './common/Player'
import { calculateNextPosition, checkIfWalkable, clampNextPosition, filterLocationEventBlueprints, findCollidingInstance, instantiateAsyncMapEvents, instantiateStillMapEvents, instantiateSyncMapEvents, moveEventInstances, splitLocationEventBlueprints } from 'src/utils/locationUtils'
import { type SyncMapEventInstance, type ControllerDirection, type MapEventBlueprint, type StillMapEventInstance, type AsyncMapEventInstance, type PlayerCoordinates } from 'src/types'
import { setVariable } from 'src/store/save/actions'
import { setSwitch } from 'src/store/navigation/actions'
import { EventDataContext } from 'src/store/events/Store'
import { SaveDataContext, SaveDispatchContext } from 'src/store/save/Store'
import { NavigationDataContext, NavigationDispatchContext } from 'src/store/navigation/Store'
import useLocation from 'src/hooks/useLocation'
import useSharedStore from 'src/hooks/useSharedStore'
import LocationEvent from './common/LocationEvent'


const container = {
  position: 'absolute',
  top: '64px',
  left: 0,
  zIndex: 1,
  backgroundColor: 'rgba(0,0,0,0.5)',
  height: 'calc(100% - 64px)',
  maxHeight: 'calc(100% - 128px)',
  width: '100vw',
  maxWidth: '400px'
}

const containerMobile = {
  ...container,
  maxWidth: '100vw'
}

const wrapper = {
  margin: 'auto',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  height: 'fit-content',
  width: 'fit-content',
  position: 'relative'
}

const overlayStyle = {
  zIndex: 3,
  position: 'absolute',
  top: 0,
  left: 0
}

const Map = () => {
  // setup 
  const [isMobile] = useState(window.innerWidth <= 768)
  const [eventsLoaded, setEventsLoaded] = useState(false)

  // local event management
  const [filteredEventBlueprints, setFilteredEventBlueprints] = useState<MapEventBlueprint[]>([])
  const [stillEventInstances, setStillEventInstances] = useState<StillMapEventInstance[]>([])
  const [syncEventInstances, setSyncEventInstances] = useState<SyncMapEventInstance[]>([])
  const [asyncEventInstances, setAsyncEventInstances] = useState<AsyncMapEventInstance[]>([])

  // list of IDs not to render
  const [deadEventIDs, setDeadEventIDs] = useState<string[]>([])
  const [sparedEventIDs, setSparedEventIDs] = useState<string[]>([])
    
  // context
  const { map } = useContext(MapDataContext)
  const { dispatchMap } = useContext(MapDispatchContext)
  const { events } = useContext(EventDataContext)
  const { save } = useContext(SaveDataContext)
  const { dispatchSave } = useContext(SaveDispatchContext)
  const { dispatchNavigation } = useContext(NavigationDispatchContext)

  // location from ctx
  const { location } = useLocation()

  // cross-content event dispatching  
  const { checkConditions, processEvent } = useSharedStore()

  // GRID // Map // location
  const { player_x, player_y, tile_size } = map.counters
  const { map_loaded, movement_blocked } = map.switches
  const { lightGrid, contentGrid } = map
  const playerTileRef = useRef<HTMLUListElement | null>(null)
  
  // Debug logging for map data
  useEffect(() => {
    if (map_loaded && contentGrid) {
      console.log('🔍 MAP DEBUG INFO:')
      console.log('Map loaded:', map_loaded)
      console.log('Content grid dimensions:', contentGrid.length, 'x', contentGrid[0]?.length)
      console.log('Content grid sample:', contentGrid.slice(0, 3).map(row => row.slice(0, 5)))
      console.log('Light grid sample:', lightGrid?.slice(0, 3).map(row => row.slice(0, 5)))
      console.log('Player position:', player_x, player_y)
      console.log('Tile size:', tile_size)
    }
  }, [map_loaded, contentGrid, lightGrid, player_x, player_y, tile_size])
 
  // getting the events
  // re-initialises map state whenever location_id || location changes
  // sets map_loaded to true
  // sets the tile_size
  useLoadMapGrid()

  useEffect(() => {
    setEventsLoaded(false)
    setStillEventInstances([])
    setSyncEventInstances([])
    setAsyncEventInstances([])
    setDeadEventIDs([])
    setSparedEventIDs([])
    
    const locationEventBlueprints = location?.events || []
    const filteredEventBlueprints = filterLocationEventBlueprints({locationEventBlueprints, events, checkConditions})
    setFilteredEventBlueprints(filteredEventBlueprints)
  }, [location])

  // loads the events whenver the map is loaded
  useEffect(() => {
    if (!eventsLoaded) {
      const { stillEventBlueprints, syncEventBlueprints, asyncEventBlueprints } = splitLocationEventBlueprints(filteredEventBlueprints)
      const newStillEventInstances = instantiateStillMapEvents(stillEventBlueprints)
      const newSyncEventInstances = instantiateSyncMapEvents(syncEventBlueprints)
      const newAsyncEventInstances = instantiateAsyncMapEvents(asyncEventBlueprints)

      setStillEventInstances(newStillEventInstances)
      setSyncEventInstances(newSyncEventInstances)
      setAsyncEventInstances(newAsyncEventInstances)
      setEventsLoaded(true)
    }
  }, [map_loaded, eventsLoaded])

 
  // events are reset whenever combat or events start / end or menu opens / closes
  // checks for events that should be reset

  const { navigation } = useContext(NavigationDataContext)
  const { menu_open, event_open, combat_open } = navigation
  useEffect(() => {
    if (map_loaded) {
      const { stillEventBlueprints, syncEventBlueprints, asyncEventBlueprints } = splitLocationEventBlueprints(filteredEventBlueprints)
      
      const newStillEventInstances = instantiateStillMapEvents(stillEventBlueprints)
      const newSyncEventInstances = instantiateSyncMapEvents(syncEventBlueprints)
      const newAsyncEventInstances = instantiateAsyncMapEvents(asyncEventBlueprints)

      // optimise this so it only compares the x, y values
      if (JSON.stringify(newStillEventInstances) !== JSON.stringify(stillEventInstances)) {
        setStillEventInstances(newStillEventInstances)
      }

      if (JSON.stringify(newSyncEventInstances) !== JSON.stringify(syncEventInstances)) {
        setSyncEventInstances(newSyncEventInstances)
      }

      if (JSON.stringify(newAsyncEventInstances) !== JSON.stringify(asyncEventInstances)) {
        setAsyncEventInstances(newAsyncEventInstances)
      }
    }
  }, [menu_open, event_open, combat_open])


  // catches events that triger a map update
  useEffect(() => {
    if (!map_loaded) {
      setEventsLoaded(false)
    }
  }, [contentGrid, lightGrid, map_loaded])

  // Remove defeated enemies from the map, but keep spared/run enemies visible
  useEffect(() => {
    if (navigation.enemy_dead) {
      // Get the current fight ID from save data
      const currentFightID = save.variables?.fight_id
      if (currentFightID) {
        // Add the defeated enemy event to deadEventIDs so it won't render
        handleDeleteEvent(currentFightID)
        console.log(`🗑️ Removed defeated enemy event: ${currentFightID}`)
      }
    }
  }, [navigation.enemy_dead])

  // Track spared enemies
  useEffect(() => {
    if (navigation.enemy_spared) {
      const currentFightID = save.variables?.fight_id
      if (currentFightID) {
        handleEnemySpared(currentFightID)
        console.log(`🤝 Enemy spared: ${currentFightID}`)
      }
    }
  }, [navigation.enemy_spared])



  // Track when combat closes to handle run scenarios
  useEffect(() => {
    if (!navigation.combat_open && save.variables?.fight_id) {
      // Combat just closed, check if we have a fight_id that wasn't spared or killed
      const currentFightID = save.variables.fight_id
      if (currentFightID && !deadEventIDs.includes(currentFightID) && !sparedEventIDs.includes(currentFightID)) {
        // This was likely a run scenario, don't mark as non-interactive
        // Run enemies should remain interactive for future encounters
        console.log(`🏃 Enemy escaped, remains interactive: ${currentFightID}`)
      }
    }
  }, [navigation.combat_open])

  // Reset enemy states when traveling to new locations
  useEffect(() => {
    const currentLocation = save.variables?.location_id
    if (currentLocation && currentLocation !== location?.id) {
      // Player traveled to new location, reset all enemy states
      setDeadEventIDs([])
      setSparedEventIDs([])
      console.log('🗺️ Traveled to new location, reset enemy states')
    }
  }, [save.variables?.location_id, location?.id])

  // processes rest events
  useResting()

  // Track defeated enemies
  const handleEnemyDefeated = (enemyId: string) => {
    setDeadEventIDs(prev => [...prev, enemyId])
  }

  // Track spared enemies
  const handleEnemySpared = (enemyId: string) => {
    setSparedEventIDs(prev => [...prev, enemyId])
  }

  // deletes events
  // refreshes on Map load
  const handleDeleteEvent = (eventID: string) => {
    if (!deadEventIDs.includes(eventID)) {
      setDeadEventIDs([...deadEventIDs, eventID])
    }
  }

  const handlePlayerMovement = (direction: ControllerDirection) => {
    if (!map_loaded || movement_blocked) { return } // avoids moving when map hasn't been loaded
    const { nextY, nextX } = calculateNextPosition(player_y, player_x, direction)

    const { clampedY, clampedX } = clampNextPosition(nextY, nextX, contentGrid) // limits the position to the map borders

    const collidingInstance = findCollidingInstance(clampedX, clampedY, stillEventInstances, syncEventInstances, asyncEventInstances)
    if (collidingInstance && !deadEventIDs.includes(collidingInstance.id) && !sparedEventIDs.includes(collidingInstance.id)) {
      processEvent(collidingInstance)
      if (collidingInstance.type === 'fight') {
        // Don't delete fight events immediately - let combat outcome determine what happens
      }
    } else {
      if (checkIfWalkable(contentGrid[clampedY][clampedX]) && (clampedY !== player_y || clampedX !== player_x)) { // checks if walkable and walks
        setPlayerPosition(dispatchMap, clampedY, clampedX)
      }
    }
    handleEventsMovement({player_x: clampedX, player_y: clampedY})
  }

  const handleEventsMovement = ({player_x, player_y}: PlayerCoordinates) => {
    const updatedSyncInstances = moveEventInstances(syncEventInstances.filter(({id}) => !deadEventIDs.includes(id) && !sparedEventIDs.includes(id)), location)
    const updatedAsyncInstances = moveEventInstances(asyncEventInstances.filter(({id}) => !deadEventIDs.includes(id) && !sparedEventIDs.includes(id)), location)

    const collidingInstance = (
      stillEventInstances.find(instance => instance.x === player_x && instance.y === player_y) ||
      updatedSyncInstances.find(instance => instance.x === player_x && instance.y === player_y) ||
      updatedAsyncInstances.find(instance => instance.x === player_x && instance.y === player_y)
    )  
    
    if (collidingInstance && !deadEventIDs.includes(collidingInstance.id) && !sparedEventIDs.includes(collidingInstance.id)) {
      processEvent(collidingInstance)
      if (collidingInstance.type === 'fight') {
        // Don't delete fight events immediately - let combat outcome determine what happens
      }
    } else {
      setSyncEventInstances(updatedSyncInstances)
      // @ts-expect-error // haven't fixed the type but it works
      setAsyncEventInstances(updatedAsyncInstances)
    }
  }

  const handleEventClick = (eventInstance: MapEventBlueprint) => {
    const { x, y, type, id } = eventInstance
    if (Math.abs(player_x - x) < 2 && Math.abs(player_y - y) < 2) {
      // Don't process spared events
      if (sparedEventIDs.includes(id)) {
        return
      }
      
      if (type === 'event') {
        setVariable(dispatchSave, 'event_id', id)
        setSwitch(dispatchNavigation, 'event_open', true)
      } else if (type === 'fight') {
        setVariable(dispatchSave, 'fight_id', id)
        setSwitch(dispatchNavigation, 'combat_open', true)
        // Don't delete the event immediately - let combat outcome determine what happens
      } else if (type === 'stash') {
        setSwitch(dispatchNavigation, 'stash_open', true)
      }
    }
  }

  // handle movement events
  const {handleTouchStart, handleTouchMove, handleTouchEnd} = usePlayerControls({handlePlayerMovement, handleEventsMovement})
  // development build logs
  useEffect(() => {
    if (process.env.REACT_APP_BENCHMARK === 'true') { console.log('Map mounted') }
    return process.env.REACT_APP_BENCHMARK === 'true' ? () => console.log('Map unmounted') : () => {}
  }, [])

  useEffect(() => {
    if (process.env.REACT_APP_BENCHMARK === 'true') { console.log('Map rendered') }
  })

  return (
    <Box sx={isMobile ? containerMobile : container} className="map" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
      { map_loaded && eventsLoaded ? (
        <Box sx={wrapper} className='map-wrapper'>
          <Box className='map-layers' sx={{position: 'relative', display: 'inline-block', height: `${tile_size * contentGrid.length}px`, width: `${tile_size * contentGrid[0].length}px`}}>
            <Box className='map-floor' sx={{ position: 'absolute', zIndex: 0}}>
              {
                contentGrid.map((_, y) => (
                  <Grid container key={y} spacing={0} wrap="nowrap">
                    {contentGrid[y].map((node, x) =>
                      <Tile
                        key={`${y}.${x}`}
                        content={''}
                        light={lightGrid[y][x]}
                        tileSize={tile_size}
                        handleClick={() => {}}
                      />
                    )}
                  </Grid>
                ))
              }
            </Box>
            <Box className='map-content' sx={{ position: 'absolute', zIndex: 2}}>
              {
                contentGrid.map((_, y) => (
                  <Grid container key={y} spacing={0} wrap="nowrap">
                    {contentGrid[y].map((node, x) =>
                      <Tile
                        key={`${y}.${x}`}
                        content={y === player_y && x === player_x ? ' ' : node}
                        light={lightGrid[y][x]}
                        transparentBackground={true}
                        tileSize={tile_size}
                        handleClick={() => {}}
                      />
                    )}
                  </Grid>
                ))
              }
            </Box>
          </Box>
          <Box sx={overlayStyle} className='map-events'>
          
            <Box style={{position: 'relative'}}>

              { stillEventInstances.map((eventBluePrint, index) => !deadEventIDs.includes(eventBluePrint.id) ? (
                <LocationEvent
                  key={index}
                  content={eventBluePrint.ui ? eventBluePrint.ui : ' '}
                  color={eventBluePrint.color}
                  tileLight={lightGrid[eventBluePrint.y][eventBluePrint.x]}
                  tileSize={tile_size}
                  handleClick={() => handleEventClick(eventBluePrint)}
                  y={eventBluePrint.y}
                  x={eventBluePrint.x}
                  spriteID={eventBluePrint.spriteID}
                />
              ) : null
              )}

              { syncEventInstances.map((eventBluePrint, index) => !deadEventIDs.includes(eventBluePrint.id) ? (
                <LocationEvent
                  key={index}
                  content={eventBluePrint.ui ? eventBluePrint.ui : ' '}
                  color={eventBluePrint.color}
                  tileLight={lightGrid[eventBluePrint.y][eventBluePrint.x]}
                  tileSize={tile_size}
                  handleClick={() => handleEventClick(eventBluePrint)}
                  y={eventBluePrint.y}
                  x={eventBluePrint.x}
                  spriteID={eventBluePrint.spriteID}
                />
              ) : null
              )}

              { asyncEventInstances.map((eventBluePrint, index) => !deadEventIDs.includes(eventBluePrint.id) ? (
                <LocationEvent
                  key={index}
                  content={eventBluePrint.ui ? eventBluePrint.ui : ' '}
                  color={eventBluePrint.color}
                  tileLight={lightGrid[eventBluePrint.y][eventBluePrint.x]}
                  tileSize={tile_size}
                  handleClick={() => handleEventClick(eventBluePrint)}
                  y={eventBluePrint.y}
                  x={eventBluePrint.x}
                  spriteID={eventBluePrint.spriteID}
                />
              ) : null
              )}
            </Box>
          </Box>
          <Player
            playerTileRef={playerTileRef}
            tileSize={tile_size}
            y={player_y}
            x={player_x}
            spriteID='boy'
          />
        </Box>) : null }
    </Box>
  )
}

export default Map
